import { Controller } from "stimulus";
import { Modal } from "bootstrap";

const updateSelectedTeachingResources = (json) => {
  const ul = document.getElementById("evt-selected-teaching-resource-list");
  if (!ul) return;

  ul.innerHTML = "";
  (json.teaching_resources || []).forEach((item) => {
    const li = document.createElement("li");
    li.className = "list-group-item evt-scenario-select";
    li.textContent = item.name;
    li.setAttribute("data-id", item.id);

    const span = document.createElement("span");
    span.className = "pull-right";
    const icon = document.createElement("i");
    icon.className = "fa fa-trash";
    icon.setAttribute(
      "data-action",
      "click->event-scenario-select#removeTeachingResource"
    );
    span.appendChild(icon);
    li.appendChild(span);
    ul.appendChild(li);
  });
};

export default class extends Controller {
  static targets = [
    "modal",
    "resourceForm",
    "error",
    "nameInput",
    "descriptionInput",
    "filesInput",
    "filesSummary",
    "vimeoGroup",
    "vimeoInput",
    "resourceTypeInput",
    "typeLabel",
    "addButton",
    "typeSelect",
    "scopeEventOnly",
    "scopeLibrary",
    "submitButton",
  ];

  static values = {
    eventId: String,
    canCreate: Boolean,
  };

  connect() {
    if (this.hasModalTarget) {
      this.modal = Modal.getOrCreateInstance(this.modalTarget);
    }

    this.onTypeChange();

    if (this.hasTypeSelectTarget) {
      this.typeSelectTarget.addEventListener("change", () => this.onTypeChange());
    }

    if (this.hasFilesInputTarget) {
      this.filesInputTarget.addEventListener("change", () => this.updateFilesSummary());
    }
  }

  disconnect() {
    if (this.modal) {
      this.modal.dispose();
      this.modal = null;
    }
  }

  onTypeChange() {
    if (!this.hasTypeSelectTarget || !this.hasAddButtonTarget) return;

    const selectedType = this.typeSelectTarget.value;
    const isCreatable =
      this.canCreateValue &&
      selectedType &&
      selectedType !== "C-MAC Video" &&
      this.eventIdValue !== "new";

    this.addButtonTarget.classList.toggle("d-none", !isCreatable);

    if (this.hasTypeLabelTarget && selectedType) {
      this.typeLabelTarget.textContent = selectedType;
    }
  }

  openModal(event) {
    event.preventDefault();

    if (!this.hasTypeSelectTarget || !this.modal || !this.hasResourceFormTarget) {
      return;
    }

    const selectedType = this.typeSelectTarget.value;
    if (!selectedType || selectedType === "C-MAC Video") return;

    this.clearError();
    this.resetResourceForm();
    this.scopeEventOnlyTarget.checked = true;
    this.resourceTypeInputTarget.value = selectedType;
    this.typeLabelTarget.textContent = selectedType;
    this.toggleVimeoField(selectedType);
    this.updateFilesSummary();
    this.modal.show();
  }

  closeModal() {
    if (this.modal) {
      this.modal.hide();
    }
  }

  resetResourceForm() {
    this.resourceFormTarget
      .querySelectorAll("input, textarea, select")
      .forEach((field) => {
        if (field.type === "file") {
          field.value = "";
        } else if (field.type === "radio" || field.type === "checkbox") {
          field.checked = field.defaultChecked;
        } else {
          field.value = field.defaultValue;
        }
      });
  }

  buildFormData() {
    const data = new FormData();

    this.resourceFormTarget
      .querySelectorAll("input, textarea, select")
      .forEach((field) => {
        if (!field.name || field.disabled) return;

        if (field.type === "file") {
          Array.from(field.files).forEach((file) => data.append(field.name, file));
        } else if (field.type === "radio") {
          if (field.checked) data.append(field.name, field.value);
        } else if (field.type === "checkbox") {
          if (field.checked) data.append(field.name, field.value);
        } else {
          data.append(field.name, field.value);
        }
      });

    return data;
  }

  toggleVimeoField(resourceType) {
    const showVimeo = resourceType === "Video";
    this.vimeoGroupTarget.classList.toggle("d-none", !showVimeo);

    if (!showVimeo && this.hasVimeoInputTarget) {
      this.vimeoInputTarget.value = "";
    }
  }

  updateFilesSummary() {
    if (!this.hasFilesInputTarget || !this.hasFilesSummaryTarget) return;

    const files = Array.from(this.filesInputTarget.files || []);

    if (files.length === 0) {
      this.filesSummaryTarget.textContent = "";
      this.filesSummaryTarget.classList.add("d-none");
      return;
    }

    const noun = files.length === 1 ? "file" : "files";
    this.filesSummaryTarget.textContent = `${files.length} ${noun} selected`;
    this.filesSummaryTarget.classList.remove("d-none");
  }

  async submit(event) {
    event.preventDefault();
    this.clearError();
    this.submitButtonTarget.disabled = true;

    const formData = this.buildFormData();
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch(
        `/events/${this.eventIdValue}/create_teaching_resource`,
        {
          method: "POST",
          body: formData,
          headers: {
            "X-CSRF-Token": csrfToken,
            Accept: "application/json",
          },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        updateSelectedTeachingResources(data);
        this.closeModal();
        this.reloadAvailableList();
      } else {
        this.showError(data.error || "Unable to create resource.");
      }
    } catch (_error) {
      this.showError("Unable to create resource. Please try again.");
    } finally {
      this.submitButtonTarget.disabled = false;
    }
  }

  reloadAvailableList() {
    if (!this.hasTypeSelectTarget) return;
    this.typeSelectTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("d-none");
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("d-none");
  }
}
